import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "cashpark_user_token";

export const userApi = axios.create({ baseURL: API });
userApi.interceptors.request.use((config) => {
  const t = localStorage.getItem(TOKEN_KEY);
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

export const setUserToken = (t) => {
  if (t) localStorage.setItem(TOKEN_KEY, t);
  else localStorage.removeItem(TOKEN_KEY);
};
export const getUserToken = () => localStorage.getItem(TOKEN_KEY);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const UserAuthCtx = createContext(null);

export function UserAuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  const refresh = useCallback(async () => {
    if (!getUserToken()) { setUser(null); setChecking(false); return; }
    try {
      const { data } = await userApi.get("/auth/me");
      setUser(data);
    } catch {
      setUserToken(null);
      setUser(null);
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const register = async (payload) => {
    const { data } = await userApi.post("/auth/register", payload);
    if (data?.token) setUserToken(data.token);
    setUser(data);
    return data;
  };
  const login = async (email, password) => {
    const { data } = await userApi.post("/auth/login", { email, password });
    if (data?.token) setUserToken(data.token);
    setUser(data);
    return data;
  };
  const logout = async () => {
    try { await userApi.post("/auth/logout"); } catch {}
    setUserToken(null);
    setUser(null);
  };

  return (
    <UserAuthCtx.Provider value={{ user, checking, register, login, logout, refresh }}>
      {children}
    </UserAuthCtx.Provider>
  );
}

export const useUserAuth = () => useContext(UserAuthCtx);
